/* eslint-disable ember/classic-decorator-hooks */
import EmberObject from '@ember/object';

import FilterConstants from '../../../../filter-constants';
import * as CommonUtils from '../../../../utils/common';
import * as CircuitUtils from '../../../utils/circuit';
import ActualFilterAmplitude from './actual-filter-amplitude';
import NoiseGroup from './noise-group';
import IdealOpAmp from './ideal-op-amp';
import DeliyannisFriendCircuit from './circuit';
import * as DeliyannisFriendCircuitLimitsUtils from './circuit-limits-utils';
import * as ComponentFactory from '../../../components/components-factory';
import NoPositiveFeedback from './component-feedbacks/no-positive-feedback';
import PositiveFeedback from './component-feedbacks/positive-feedback';
import { getImplementation } from '../../../gestures/optimization';
import { calculateFilterComponents } from '@ebiz/aditools-filter';
import { set } from '@ember/object';

// This class contains the logic on how a multiple feedback band pass / deliyannis-friend band pass is created.
// A circuit is created from the stage properties stageFp, stageQ, and gain, and the optimization;
export default class DeliyannisBandPassCircuit extends EmberObject {
  // Inputs on create
  stageFp;
  stageQ;
  gain;
  optimization;
  algorithm;

  // Process output - something used internally but on other classes
  supportedComponents;
  actualFilterAmplitudeModel;
  noiseGroupModel;
  idealOpAmpModel;
  circuitModel;
  idealOpAmpValuePaths;
  q0;
  k;
  sizing;
  frequency;

  // Figures output - used internally and also as output in the figures
  needReferenceStage = true;
  c1;
  c2;
  r1;
  r2;
  r3;
  r4;
  r5;
  masterCapacitor;
  allowGainChange;

  // For cGov params of calculateFilterComponents - aditools-filter
  c1IdealValue;

  init() {
    super.init();

    if (
      CommonUtils.isNullOrUndefined(this.stageFp) ||
      CommonUtils.isNullOrUndefined(this.stageQ) ||
      CommonUtils.isNullOrUndefined(this.gain) ||
      CommonUtils.isNullOrUndefined(this.optimization) ||
      CommonUtils.isNullOrUndefined(this.algorithm)
    ) {
      throw 'stageFp, stageQ, gain, optimization, and algorithm should not be null';
    }

    this.actualFilterAmplitudeModel = ActualFilterAmplitude;
    this.noiseGroupModel = NoiseGroup;
    this.idealOpAmpModel = IdealOpAmp;
    this.circuitModel = DeliyannisFriendCircuit;
    this.supportedComponents = ['c1', 'c2', 'r1', 'r2', 'r3', 'r4', 'r5'];
    this.idealOpAmpValuePaths = ['r5', 'q0', 'k'];

    if (!CommonUtils.isNullOrUndefined(this.initialCapacitorValue)) {
      this.c1IdealValue = this._computeC1IdealValue(this.initialCapacitorValue);
    } else {
      this.c1IdealValue = this._computeC1IdealValue();
    }
    const idealValues = this._computeIdealValues();
    const capacitorLimits = DeliyannisFriendCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
      idealValues.r5IdealValue,
    );
    const gainResistorLimits = DeliyannisFriendCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits, gainResistorLimits);
  }

  _computeIdealValues() {
    const implementation = getImplementation(FilterConstants.implementationType.deliyannisFriend);
    const rGain = this._getFeedback().r3Value + this._getFeedback().r4Value;
    const c1IdealValue = this.c1IdealValue;
    const params = {
      type: this.algorithm,
      implementation: implementation,
      fp: this.stageFp,
      gain: this.gain,
      q: this.stageQ,
      cGov: c1IdealValue,
      rGainOrCCross: rGain,
    };
    const filterComponents = calculateFilterComponents(
      params.type,
      params.implementation,
      params.fp,
      params.gain,
      params.q,
      params.cGov,
      params.rGainOrCCross,
    );

    return {
      c1IdealValue: filterComponents.c1,
      c2IdealValue: filterComponents.c2,
      r1IdealValue: filterComponents.r1,
      r2IdealValue: filterComponents.r2,
      r3IdealValue: filterComponents.r3,
      r4IdealValue: filterComponents.r4,
      r5IdealValue: filterComponents.r5,
    };
  }

  _getFeedback() {
    const a = this.gain / (2 * this.stageQ * this.stageQ);
    const r3PlusR4 = this.optimization.determineStartingR3R4Value(this.stageFp);
    let result = null;
    if (this.stageQ < 10 && a < 1) {
      result = NoPositiveFeedback.create({ stageQ: this.stageQ, gain: this.gain });
    } else {
      result = PositiveFeedback.create({ stageQ: this.stageQ, a: a, gain: this.gain, r3PlusR4: r3PlusR4 });
    }

    return result;
  }

  _computeR3R4Gain() {
    return 1 / this._getFeedback().k;
  }

  _computeC1IdealValue(value) {
    return value ? value : CircuitUtils.computeMasterCapacitorValue(this.optimization, this.stageFp);
  }

  // Private
  // computes for the the output values.
  _computeComponents(idealValues, capacitorLimits, gainResistorLimits) {
    this.sizing = this._getSizing(idealValues, capacitorLimits);
    let r3NominalValue = ComponentFactory.createR3(idealValues.r3IdealValue, null, null).toCommercialComponent().value;
    this.allowGainChange = CircuitUtils.getGainChangeSupportStatus(r3NominalValue, idealValues.r4IdealValue);
    this.c1 = ComponentFactory.createC1(
      idealValues.c1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
    );
    this.masterCapacitor = this.c1;
    let rcRatioInverse = CircuitUtils.getInverseOfMasterCapacitorRatio(this.c1);
    this.c2 = ComponentFactory.createC2(
      idealValues.c2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.r2 = this._getR2(idealValues, capacitorLimits, rcRatioInverse);
    this.r3 = ComponentFactory.createR3(
      r3NominalValue,
      gainResistorLimits.resistorMinimumLimitMultiplier,
      gainResistorLimits.resistorMaximumLimitMultiplier,
    );
    this.r4 = this._getR4(idealValues, capacitorLimits);
    this.r5 = ComponentFactory.createR5(
      idealValues.r5IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      rcRatioInverse,
    );
    this.q0 = { value: this._getFeedback().q0 };
    this.k = { value: this._getFeedback().k };
    this.frequency =
      1 /
      (2 *
        Math.PI *
        Math.sqrt(CommonUtils.parallel(this.r1.value, this.r2.value) * this.r5.value * this.c1.value * this.c2.value));

    this.idealValues = idealValues;
    this.capacitorLimits = capacitorLimits;
    this.gainResistorLimits = gainResistorLimits;
  }

  // Private
  // Gets the sizing
  _getSizing(idealValues, capacitorLimits) {
    let r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
    );
    return CircuitUtils.getResistorSize(r1);
  }

  // Private
  // Computes for the r2
  _getR2(idealValues, capacitorLimits, rcRatioInverse) {
    var result = null;
    if (CommonUtils.isNearInfinity(idealValues.r2IdealValue))
      result = ComponentFactory.createR2MinMaxDefined(
        idealValues.r2IdealValue,
        FilterConstants.Limits.INFINITY,
        FilterConstants.Limits.INFINITY,
        this.sizing,
        rcRatioInverse,
      );
    else
      result = ComponentFactory.createR2(
        idealValues.r2IdealValue,
        capacitorLimits.capacitorMinimumLimitMultiplier,
        capacitorLimits.capacitorMaximumLimitMultiplier,
        this.sizing,
        rcRatioInverse,
      );

    return result;
  }

  // Private
  // Computes for the R4. This is dependent on the value of R3
  // R4 may have an ideal value but it is recomputed here based on gain and R3
  // since it should adjust depending on the current value of R3.
  // (If r3 is not present or idealValue is 0 or INFINITY, we use the idealValue)
  // R3 may change due to the user changing the slider position or r3 adjusting to its nominal value.
  // R3 may also change when changing the e-series in the tolerance tab.
  _getR4(idealValues, gainResistorLimits) {
    let result = null;
    if (idealValues.r3IdealValue !== 0 && idealValues.r3IdealValue !== FilterConstants.Limits.INFINITY) {
      result = ComponentFactory.createR4FromR3AndGain(
        this.r3.value,
        this._computeR3R4Gain(),
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );
    } else {
      result = ComponentFactory.createR4(
        idealValues.r4IdealValue,
        gainResistorLimits.resistorMinimumLimitMultiplier,
        gainResistorLimits.resistorMaximumLimitMultiplier,
      );
    }

    return result;
  }

  // Public
  // function used for the gain slider.
  // This accepts a new value of the r3 and computes for the new value of R4.
  changeGainValues(newR3Value) {
    if (this.allowGainChange) {
      this.r3 = this.r3.setValue(newR3Value);
      this.updateR4();
    }
  }

  updateR4() {
    set(this, 'r4', this._getR4(this.idealValues, this.gainResistorLimits));
  }

  // Public
  // function used when the RC slider is changed.
  changeInitialCapacitor(value) {
    this.c1IdealValue = this._computeC1IdealValue(value);
    const idealValues = this._computeIdealValues();
    const capacitorLimits = DeliyannisFriendCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
      idealValues.r5IdealValue,
    );
    const gainResistorLimits = DeliyannisFriendCircuitLimitsUtils.computeGainResistorLimitMultipliers(
      idealValues.r3IdealValue,
      idealValues.r4IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits, gainResistorLimits);
  }
}
